<script setup lang="ts">
/* The frame every page sits in: the side menu on the left (the Studio mark, the
 * workspace switcher and the publications), and on the right a breadcrumb to
 * where you are (the module, then the open issue) and who is signed in.
 *
 * The "Studio" brand and the workspace switcher used to sit in this top bar.
 * Both are in the side menu now, which is the confirmed 1C-2 shell, so what is
 * left here is the breadcrumb and the account menu. */
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { request } from "@/lib/api";
import SideMenu from "@/components/SideMenu.vue";
import AccountMenu from "@/components/AccountMenu.vue";
import { reviewTabKey, type ReviewTab } from "@/lib/shell-measure";
import { publicationHomeTarget, resolvePublication, type PublicationIdentity, type PublicationSummary } from "@/lib/publication-route";
import { isPhoneMenu, menuFoldShortcut, type MenuFoldScope } from "@/lib/menu-fold";

const router = useRouter();
const route = useRoute();

const personName = ref("");
const userId = ref("");
const role = ref("");
const issueTitle = ref("");
const publication = ref<PublicationIdentity | null>(null);
/* The workspace's publications, for the side menu. The menu is on every
 * workspace route, so the list is fetched on every workspace route now, not
 * only on the ones with a publication in the address. */
const issues = ref<PublicationSummary[]>([]);
/* IssueTitle renames in place; the crumb fetched the name once, so it follows the event. */
function onIssueRenamed(event: Event): void {
  const detail = (event as CustomEvent<{ id: string; title: string }>).detail;
  if (!detail) return;
  if (publication.value?.id === detail.id) issueTitle.value = detail.title;
  /* The side menu reads its titles from this list, so the rename reaches it too
   * (R2 review 3 Nov: the crumb followed a rename while the menu kept the old title). */
  if (issues.value.some((item) => item.id === detail.id)) {
    issues.value = issues.value.map((item) => (item.id === detail.id ? { ...item, title: detail.title } : item));
  }
}
onMounted(() => window.addEventListener("studio:issue-renamed", onIssueRenamed));
onUnmounted(() => window.removeEventListener("studio:issue-renamed", onIssueRenamed));

/* Two measures, both taken from the mocks. Most screens are drawn inside
 * `.wrap{max-width:1000px}`, but composer.html and share-view.html widen that
 * same wrap to 1240px, so the page view gets the room its proof needs.
 *
 * CORRECTED. This used to read `route.name === "review"`, which widened the
 * whole Review family. But the Review family is three screens on one route and
 * only ONE of them is drawn wide: page-status.html:26 (the contact sheet) and
 * feedback-view.html:28 (the feedback list) are both `max-width:1000px`. The
 * error hid because both measures resolve to the same gutter in a 1000px
 * screenshot; on a 1400px monitor the contact sheet laid out nine columns of
 * chips where the design draws seven.
 *
 * `--pad` is resolved on `.shell__body` from `--measure`, so a descendant
 * setting `--measure` would be read too late. The shell therefore owns the tab
 * and ReviewView writes to it (see lib/shell-measure.ts). The route is still
 * part of the test, so a tab left on "page" cannot widen some other route. */
const reviewTab = ref<ReviewTab>("page");
provide(reviewTabKey, reviewTab);

const onReview = computed(() => route.name === "review" || route.name === "review-version" || route.name === "handout-review" || route.name === "handout-review-version");
const onPlan = computed(() => route.name === "plan");
/* The upload report wears Review's tabs (preflight.html:218, repair-report.html:231) and its
 * crumb ends in "Review" as theirs does (repair-report.html:227), so it is Review's frame too. */
const onReport = computed(() => route.name === "upload-report" || route.name === "handout-upload-report");
/* SUPERSEDED 2026-09-24, owner: switching Page, Contact sheet and Feedback
 * made the whole screen jump, the header, tabs and search moving about 120px
 * inward at 1440px because two tabs sat in the 952px frame and one in 1192px
 * ("one was centre-aligned while the other is edge-to-edge"). All three tabs
 * now share the wide frame, so only the content under the tabs changes; the
 * contact sheet lays out more columns and the feedback list runs wider. The
 * tab channel stays: ReviewView still writes it; nothing sizes from it now. */
const wide = computed(() => onReview.value || onPlan.value || onReport.value);
const reviewPage = computed(() => onReview.value && reviewTab.value === "page");
/* The menu's fold is remembered separately for the proof page and for every
 * other page, because the proof page is where the room is worth having back. */
const menuScope = computed<MenuFoldScope>(() => (reviewPage.value ? "proof" : "rest"));

/* The phone tier, row R5 of the 1C-2 build (owner question 2A).
 *
 * Below 560px the side menu has no column of its own: there is no room for a
 * 248px menu beside a phone screen, and a 60px rail would still take a
 * sixth of the width. So the menu leaves the flow entirely and becomes a
 * drawer over the page, opened from a button in the top bar.
 *
 * The shell owns the drawer, and not the menu, because the button that opens
 * it lives in the top bar, which is the shell's, and because the drawer has
 * to close on things the menu knows nothing about: a route change, the Escape
 * key, and the window growing past the phone width.
 *
 * Nothing about the drawer is remembered. It is a state of this page and not
 * a setting, so nothing is written to storage for it: the fold is still
 * remembered per person, and the drawer is not remembered at all. */
const phone = ref(isPhoneMenu());
const drawerOpen = ref(false);
const menuButton = ref<HTMLButtonElement | null>(null);
function closeDrawer(): void {
  drawerOpen.value = false;
}

function toggleDrawer(): void {
  drawerOpen.value = !drawerOpen.value;
}

/* The drawer is a page state, so it closes with the page: a menu link takes
 * you somewhere else, and the drawer has no business following you there. */
watch(() => route.fullPath, closeDrawer);

/* The window growing out of the phone tier closes the drawer too, because the
 * menu is a column again at that width and there is nothing to overlay. */
function onShellResize(): void {
  const isPhone = isPhoneMenu();
  if (isPhone === phone.value) return;
  phone.value = isPhone;
  if (!isPhone) closeDrawer();
}

/* Escape closes the drawer, and the drawer is the nearest open thing on the
 * screen while it is up, so it takes the key FIRST: a capture listener on the
 * window runs before every document and window listener on the bubble path.
 * Two of those used to win (review R5, Sol): Review's own Escape on the window
 * switched Feedback to Page under the drawer, and an AccountMenu left open in
 * the top bar consumed the key on document and left the drawer up. The one
 * thing nearer than the drawer is a menu open INSIDE it (the workspace
 * switcher, role="menu"), and that keeps the key: its document handler closes
 * it and stops the event, so the drawer stays and nothing behind it moves. */
function onDrawerEscape(event: KeyboardEvent): void {
  if (event.key !== "Escape" || !phone.value || !drawerOpen.value) return;
  if (document.getElementById("side-menu")?.querySelector('[role="menu"]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  closeDrawer();
}

/* The one key that folds the menu opens and closes the drawer at the phone
 * width, where there is no fold to do: the drawer is the open menu. */
function onShellKeydown(event: KeyboardEvent): void {
  if (!phone.value) return;
  if (!menuFoldShortcut(event)) return;
  event.preventDefault();
  toggleDrawer();
}

/* The page does not scroll behind an open drawer. The value the body had is
 * put back when the drawer closes, so a page that had its own overflow keeps
 * it. */
let bodyOverflow = "";
let bodyOverflowHeld = false;
watch(drawerOpen, (open) => {
  if (open) {
    bodyOverflow = document.body.style.overflow;
    bodyOverflowHeld = true;
    document.body.style.overflow = "hidden";
    return;
  }
  if (!bodyOverflowHeld) return;
  bodyOverflowHeld = false;
  document.body.style.overflow = bodyOverflow;
});

/* The focus goes into the drawer when it opens and back to the button that
 * opened it when it closes. The button is looked up when the time comes
 * rather than remembered, because the phone tier can end while the drawer is
 * open and take the button with it. */
watch(drawerOpen, async (open) => {
  if (open) return;
  await nextTick();
  menuButton.value?.focus();
});

onMounted(() => {
  phone.value = isPhoneMenu();
  window.addEventListener("resize", onShellResize);
  window.addEventListener("keydown", onShellKeydown);
  window.addEventListener("keydown", onDrawerEscape, true);
});
onUnmounted(() => {
  window.removeEventListener("resize", onShellResize);
  window.removeEventListener("keydown", onShellKeydown);
  window.removeEventListener("keydown", onDrawerEscape, true);
  if (bodyOverflowHeld) {
    bodyOverflowHeld = false;
    document.body.style.overflow = bodyOverflow;
  }
});

/* Which screens draw their own section nav, and therefore own the hairline
 * under it. The Review family is not the only one: admin.html:286-294 puts the
 * same `.secnav` on People and Activity, with ONE `.tbrule` below it and none
 * between the topbar and the nav. The shell was drawing its own rule on every
 * non-review route, so both admin screens rendered two hairlines stacked with
 * the nav between them, where the mock draws one under the nav.
 *
 * The two families are kept apart rather than merged because their topbars
 * close differently, and both spellings are deliberate: composer.html:53 is
 * `padding:18px 28px 0`, admin.html:46 is `padding:18px 28px 14px`. Both then
 * open the nav with `.secnav{padding:14px 28px 0}`, so the gap under the brand
 * is 14px on the proof screens and 28px on the admin ones.
 *
 * `upload-report` ADDED 2026-09-17, the third family. preflight.html:218-226
 * and repair-report.html:231-237 both draw the same `.secnav` on the upload
 * report, and `UploadReportView.vue` now mounts it. Naming the route here is
 * the other half of that: SecNav draws the hairline, so without this the report
 * would show the shell's rule and the nav's rule stacked, exactly the fault the
 * admin screens had. Each half alone is worse than neither, which is why they
 * land together. */
const onSecNav = computed(() => onReview.value || onPlan.value || route.name === "members" || route.name === "history" || onReport.value);
/* Paul, 25 Sep: the issue home was reachable only from the landing's "Open issue". Below
 * it (Review, the upload report, Plan) the crumb's publication name is the way back up. */
const underReview = computed(() => onReview.value || onReport.value);
const homeLink = computed(() => workspaceId.value && publication.value ? publicationHomeTarget(workspaceId.value, publication.value) : null);

const workspaceId = computed(() => (typeof route.params.ws === "string" ? route.params.ws : null));
const maySeeAdmin = computed(() => role.value === "admin" || role.value === "publisher");
/* The publication's own address, without the route name: home, Review, a pinned
 * Review and the report of one publication share it, so a hop between them does not
 * refetch the list (as the number-only key never did). A number belongs only to a
 * magazine route and an id only to a handout route, so the family is still in it. */
const publicationAddress = computed(() => JSON.stringify([
  typeof route.params.number === "string" ? route.params.number : null,
  typeof route.params.id === "string" ? route.params.id : null,
]));
/* Synchronous from the address, as the number-only gate was: the crumb's "InPrint"
 * shows while the list is in flight and stays if the list fails. */
const addressed = computed(() => typeof route.params.number === "string" || typeof route.params.id === "string");
const adminSection = computed(() => typeof route.meta.adminSection === "string" ? route.meta.adminSection : null);

function roleLabel(value: string): string {
  return value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "";
}

const personLabel = computed(() => {
  if (!personName.value) return "";
  const label = roleLabel(role.value);
  return label ? `${personName.value} (${label})` : personName.value;
});

async function signOut(): Promise<void> {
  try {
    await request("/api/auth/sign-out", { method: "POST" });
  } finally {
    await router.push("/sign-in");
  }
}


/* Who is signed in only matters once inside a workspace (the mock draws the
 * name and role next to an open issue), so this stays request-free on every
 * route outside one, the same invariant the workspace switcher itself keeps. */
/* The role is NOT fetched here. WorkspaceSwitcher already asks /api/workspaces
 * for the list it switches between, and every membership in that answer carries
 * its role, so asking again would put two identical requests on every workspace
 * route. It reaches us on the switcher's `role` event instead. */
let sessionGeneration = 0;
watch(
  workspaceId,
  async (id) => {
    const sessionCheck = ++sessionGeneration;
    if (id === null) {
      personName.value = "";
      userId.value = "";
      role.value = "";
      return;
    }
    try {
      const session = await request<{ user?: { id?: string; name?: string } }>("/api/auth/get-session");
      if (sessionCheck !== sessionGeneration) return;
      personName.value = session.user?.name ?? "";
      /* The id is what the side menu's fold is remembered under, so two people
       * sharing a browser keep their own setting. */
      userId.value = session.user?.id ?? "";
    } catch {
      if (sessionCheck === sessionGeneration) {
        personName.value = "";
        userId.value = "";
      }
    }
  },
  { immediate: true },
);

let issueGeneration = 0;
watch(
  [workspaceId, publicationAddress],
  async ([ws], previous) => {
    const generation = ++issueGeneration;
    /* A new workspace drops the old one's list at once: while the new list is in
     * flight the menu must not link the old workspace's publications under the
     * new workspace's address, nor fetch their covers from it (R2 review 3 Nov). */
    if (previous && previous[0] !== ws) issues.value = [];
    /* The list is the side menu's, so it is fetched on every workspace route
     * and not only on the ones with a publication in the address. Only a
     * publication in the address has a crumb to name. */
    if (ws === null) {
      issueTitle.value = "";
      publication.value = null;
      issues.value = [];
      return;
    }
    try {
      const result = await request<{ issues: PublicationSummary[] }>(`/api/workspaces/${ws}/issues`);
      if (generation !== issueGeneration) return;
      issues.value = result.issues ?? [];
      const identity = resolvePublication(route, issues.value);
      publication.value = identity;
      issueTitle.value = identity ? issues.value.find((item) => item.id === identity.id)?.title ?? "" : "";
    } catch {
      if (generation === issueGeneration) {
        issueTitle.value = "";
        publication.value = null;
        issues.value = [];
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="shell" :class="{ 'shell--wide': wide, 'shell--plan': onPlan, 'shell--review-page': reviewPage, 'shell--secnav': onSecNav, 'shell--secnav-flush': onReview, 'shell--menu': workspaceId, 'shell--phone': phone }">
    <!-- The side menu, on every workspace route. It carries the Studio mark and
         the workspace switcher, which used to be in this top bar. At the phone
         width it is a drawer over the page rather than a column beside it, and
         the top bar's Menu button opens it. -->
    <SideMenu
      v-if="workspaceId"
      :workspace-id="workspaceId"
      :issues="issues"
      :user-id="userId"
      :scope="menuScope"
      :may-see-admin="maySeeAdmin"
      :phone="phone"
      :drawer-open="drawerOpen"
      @role="role = $event"
      @close="closeDrawer"
    />
    <div class="shell__main">
      <header class="topbar">
        <!-- The phone tier's way into the menu. It is the first thing in the top
             bar, where a thumb reaches for it, and it is the only way in: the
             menu is a drawer there and not a column. -->
        <button
          v-if="phone && workspaceId"
          ref="menuButton"
          class="s-btn shell__menu-button"
          type="button"
          aria-label="Open the menu"
          :aria-expanded="drawerOpen"
          aria-controls="side-menu"
          @click="toggleDrawer"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <span v-if="adminSection" class="crumb">
          <span class="mod">Admin</span>
          <span class="chev" aria-hidden="true">&rsaquo;</span>
          <span class="here" aria-current="page">{{ adminSection }}</span>
        </span>
        <!-- unnumbered-publications.html:266. The Publications landing is InPrint's
             own page, so the crumb names the module and nothing after it. -->
        <span v-else-if="route.name === 'workspace'" class="crumb">
          <span class="mod" aria-current="page">InPrint</span>
        </span>
        <span v-else-if="addressed" class="crumb">
          <!-- Paul 2026-09-26: "InPrint" is the way back to the module's landing from
               anywhere below it, as the publication name is the way back to its home. -->
          <RouterLink v-if="workspaceId" class="mod up hit" :to="{ name: 'workspace', params: { ws: workspaceId } }">InPrint</RouterLink>
          <span v-else class="mod">InPrint</span>
          <template v-if="publication?.kind === 'handout' && issueTitle">
            <span class="chev" aria-hidden="true">&rsaquo;</span>
            <span class="mod">Handouts</span>
            <span class="chev" aria-hidden="true">&rsaquo;</span>
            <RouterLink v-if="underReview && homeLink" class="up hit" :to="homeLink">{{ issueTitle }}</RouterLink>
            <span v-else-if="underReview">{{ issueTitle }}</span>
            <span v-else class="here" aria-current="page">{{ issueTitle }}</span>
            <template v-if="underReview">
              <span class="chev" aria-hidden="true">&rsaquo;</span>
              <span class="mod" aria-current="page">Review</span>
            </template>
          </template>
          <template v-else-if="issueTitle">
            <span class="chev" aria-hidden="true">&rsaquo;</span>
            <!-- On a review route the issue title is no longer the deepest
                 segment, so it gives up `.here` and `aria-current` to "Review",
                 exactly as page-status.html:226 writes it: the title is a plain
                 `<span>` there and `Review` takes `.mod`. That the deepest
                 segment is `.mod` (ink, weight 500) rather than `.here` (ink) is
                 the mock's own choice, kept. The mock omits `aria-current`
                 because a mock is never read by assistive tech; the segment that
                 names the current screen carries it here. -->
            <RouterLink v-if="(underReview || onPlan) && homeLink" class="up hit" :to="homeLink">{{ issueTitle }}</RouterLink>
            <span v-else-if="underReview || onPlan">{{ issueTitle }}</span>
            <span v-else class="here" aria-current="page">{{ issueTitle }}</span>
            <template v-if="underReview || onPlan">
              <span class="chev" aria-hidden="true">&rsaquo;</span>
              <span class="mod" aria-current="page">{{ onPlan ? "Plan" : "Review" }}</span>
            </template>
          </template>
        </span>
        <span class="spacer"></span>
        <AccountMenu v-if="personLabel" :label="personLabel" :name="personName" :admin-to="workspaceId && maySeeAdmin ? { name: 'members', params: { ws: workspaceId } } : null" @sign-out="signOut" />
        <button v-else class="sign-out hit" type="button" @click="signOut">Sign out</button>
      </header>
      <!-- issue-home.html:204. On a screen with a section nav, SecNav draws this
           rule below its own tabs instead, so only one is ever on screen. -->
      <div v-if="!onSecNav" class="tbrule"></div>
      <main class="shell__body">
        <slot />
      </main>
    </div>
    <!-- The page behind an open drawer. It closes the drawer when it is
         tapped, which is the only way out of it with a pointer. It sits after
         the main column so the shell's first two children stay the menu and
         the column beside it. -->
    <div v-if="phone && drawerOpen" class="shell__scrim" aria-hidden="true" @click="closeDrawer"></div>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-height: 100%;

  /* The approved screens are drawn 952px wide. Every mock frames its screen
   * as `<div class="app">` inside `.wrap{max-width:1000px;padding-inline:24px}`,
   * and `.app` itself sets no width, so 1000 - 48 = 952 is what the owner
   * signed off on. That the wrap is the SCREEN's measure rather than the
   * documentation page's own margin is settled by composer.html and
   * share-view.html, which widen the same wrap to 1240px instead of leaving
   * it alone: the authors moved it when a screen needed the room.
   *
   * Nothing in the app set a measure at all, so on a 1440 display every
   * screen was drawn about 450px wider than its picture, which is why the
   * three doors on the issue home sprawled and Full history sat alone in a
   * dead gap. A screen that wants the wider drawing sets --measure itself. */
  --measure: 952px;
  --gutter: 28px;
}

/* The side menu is the first column and the top bar and body the second. The
 * menu sets its own width (248px open, 60px folded), so the column is `auto`
 * and the measures below apply to the room beside it. The rule across the top
 * is the confirmed 1C-2 shell's own terracotta line. */
.shell--menu {
  grid-template-columns: auto minmax(0, 1fr);
  border-top: 3px solid var(--mod-inprint);
}

/* The phone tier (row R5). The menu has no column here: there is no room for
 * a 248px menu beside a phone screen, so the shell is one column and the menu
 * is a drawer over it (SideMenu.vue draws the drawer). The rule comes after
 * .shell--menu because the two have the same weight and this one is the later
 * truth at this width. */
.shell--phone {
  grid-template-columns: minmax(0, 1fr);
}

/* The right-hand column: the top bar, its rule and the body. It is a column
 * of its own so the body can fill the height the proof page needs. */
.shell__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* Review fills a normal wide monitor to its 28px gutters, then stops growing. */
.shell--wide { --measure: 2560px; }

/* The narrow tier, which had never been ported. The owner's Q162 answer was
 * "iPad portrait and phones", so these widths are the ones he will actually
 * hold the app in, and until now every screen kept its 28px desktop gutter all
 * the way down to a 375px phone: 56px of the 375 spent on margin.
 *
 * Twelve mocks carry a 820px tier and every one of them narrows the gutter to
 * 20px, listing it per row because a mock has no shell to put it in:
 * preflight.html:180 `.topbar,.secnav,.shead,.vrow,.lead,.vlist{padding-inline:20px}`,
 * page-status.html and feedback-view.html the same over their own row names,
 * admin.html, designer-files.html and issue-home.html as `.topbar,.head`.
 * Porting those selector lists screen by screen would spend the gutter twice,
 * the defect this shell exists to prevent, because in the build every one of
 * those rows takes its inset from `--pad`, which is computed from `--gutter`
 * just below. So the whole tier is this one declaration.
 *
 * 16px at the bottom, from versions.html:209-210. That mock puts its 16px at
 * 560px and composer.html:253-254 puts the same 16px at 640px; 560 is the
 * breakpoint eleven mocks use for their second tier, so the shell takes 560 and
 * composer's 640 stays composer's own business.
 *
 * Only the gutter is here. The per-screen work in those same blocks, grids
 * collapsing to one column, headings dropping a few points, rows turning into
 * stacks, belongs to the screens and is not the shell's to guess at. */
@media (max-width: 820px) { .shell { --gutter: 20px; } }
@media (max-width: 560px) { .shell { --gutter: 16px; } }

.topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-navbar);
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 14px;
  /* Background stays full bleed, because the topbar is the window's edge, but
   * the brand lines up with the content below it on the same gutter, as it does
   * in every mock (topbar and head are both inset 28px).
   *
   * There is deliberately NO border-bottom. Not one of the four mocks puts a
   * rule on `.topbar`: issue-home.html:204 draws a separate `<div class="tbrule">`
   * beneath it, and `.tbrule{margin:0 28px}` insets that rule by the gutter,
   * where a border here would run full bleed. On the Review screens the rule
   * moves below the section nav instead, so a border here drew a second one.
   * The element is rendered in the template, on the screens that want it. */
  padding: 18px var(--pad) 14px;
  background: var(--ground);
}

/* A screen with a section nav lets SecNav's own 14px top padding open the gap
 * under the brand, and SecNav's `.tbrule` draw the rule, so the body adds no
 * top padding of its own on any of them. */
.shell--secnav .shell__body { padding-top: 0; }
/* The proof family alone closes its topbar tight: composer.html:53 and
 * page-status.html:44 are both `padding:18px 28px 0`. admin.html:46 keeps the
 * 14px, so it is not in this rule. */
.shell--secnav-flush .topbar { padding-bottom: 0; }
/* Plan closes its top bar before its own section-nav hairline, like the
 * approved flatplan; this does not change the admin or upload-report families. */
.shell--plan .topbar { padding-bottom: 0; }

/* issue-home.html:204, the rule as its own element, inset by the gutter rather
 * than bleeding to the window edge. */
.tbrule {
  height: 1px;
  background: var(--hair);
  margin: 0 var(--pad);
}

/* Owner, 2026-09-25 (option B): every screen starts on the same left gutter,
 * so "Studio" and the page title never move between screens. The top bar and
 * its rule run the full window on all of them; a 952px screen keeps its
 * measure but sits on the left instead of centring, and the empty room goes to
 * its right. Review, Plan and the report still fill to the 2560px measure.
 * max() keeps the right gutter when the window is narrower than the measure. */
.topbar,
.tbrule,
.shell__body {
  --pad: var(--gutter);
  --pad-end: max(var(--gutter), calc(100% - var(--measure) + var(--gutter)));
}

.crumb {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 9px;
  min-width: 0;
  font-size: 14.5px;
  color: var(--muted);
}

.crumb .mod { color: var(--ink); font-weight: 500; }
.crumb .here { color: var(--ink); }
.crumb .up { color: inherit; text-decoration: none; }
.crumb .mod.up { color: var(--ink); }
.crumb .up:hover { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; }
.crumb .chev { color: var(--faint); font-size: 12px; }

.spacer { flex: 1 1 auto; }

:deep(.workspace-switcher__menu:has(.workspace-switcher__entry:only-child)) { display: none; }
.sign-out {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--muted);
  font: inherit;
  font-size: 13.5px;
  cursor: pointer;
}
.sign-out:hover { color: var(--ink); }

.shell__body {
  flex: 1;
  /* 34px above matches `.head{padding:34px 28px 0}` in the mocks. The 64px
   * below is my choice, not theirs: their bottom padding belongs to the
   * documentation page around the screen, not to the screen. */
  padding: 34px var(--pad-end) 64px var(--pad);
}
/* The Page tab is the one routed screen whose bounded work row consumes the
 * remaining shell height. The header stays in normal flow, so this follows its
 * real measured height rather than guessing at one in a viewport calculation.
 * The row is named so the column beside the menu is exactly the shell's
 * height, and the Page tab still fills to the bottom of it. */
.shell--review-page { height: 100vh; height: 100dvh; grid-template-rows: minmax(0, 1fr); }
/* ReviewView's root passes the bounded height on to the proof, which fits its page to it. */
.shell--review-page .shell__body > :deep(section) { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
.shell--review-page .shell__body { min-height: 0; display: flex; flex-direction: column; padding-bottom: 0; }

/* The phone tier's own chrome (row R5).
 *
 * The Menu button is the only way into the menu at this width, and it is a
 * square icon button so a thumb finds it: 40px by 40px, the same height as
 * every other control in the app, with the three-line icon inside it. */
.shell__menu-button {
  width: 40px;
  min-width: 40px;
  padding: 0;
  flex: none;
}
.shell__menu-button svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* The top bar at this width holds a button, a crumb and the account menu, and
 * the button is 40px tall, so the row is centred on it rather than sitting it
 * on the crumb's baseline.
 *
 * It is ONE row at this width (row R6). The bar was free to wrap, so at 375px
 * the account chip dropped onto a line of its own under the Menu button and
 * the crumb, on every screen. The crumb is the part that gives way, so the
 * row itself stops wrapping and the crumb truncates instead. */
.shell--phone .topbar {
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px 10px;
}

/* The crumb takes the room the row has left, and no more.
 *
 * Inside it, the items before the last one give ground first: they shrink
 * eight times as readily as the last item does, so the module name and the
 * issue title are what get cut when the line runs out, and the last item, the
 * name of the page you are on, keeps as much room as it can. Every item
 * truncates with an ellipsis rather than being cut off mid-glyph, and `nowrap`
 * keeps the crumb on one line, which is the whole point of the rule above.
 *
 * The module name and the chevrons never shrink at all: a half-written "InP"
 * and a squeezed chevron read as damage rather than as a choice. */
.shell--phone .crumb {
  flex: 1 1 auto;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
}
.shell--phone .crumb > * {
  flex-shrink: 8;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell--phone .crumb > :first-child,
.shell--phone .crumb > .chev {
  flex-shrink: 0;
}
.shell--phone .crumb > :last-child {
  flex-grow: 1;
  flex-shrink: 1;
}

/* The account chip is 34px of circle and never gives way to the crumb. */
.shell--phone .topbar :deep(.account) { flex: none; }
.shell--phone .sign-out { flex: none; white-space: nowrap; }

/* The scrim: the page behind an open drawer, dimmed. It is the token
 * scrim colour, and it sits above the top bar (--z-navbar) and below the
 * drawer itself (--z-overlay), so the menu is the thing you can still see. */
.shell__scrim {
  position: fixed;
  inset: 0;
  z-index: var(--z-panel);
  background: var(--scrim);
}
</style>
